import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { take } from 'rxjs';
import { DirectivesModule } from '../../../../directives/directives.module';
import { UserData } from '../../../../dtos/response/login-response/user-data';
import { initializeInitialBookingState } from '../../../../features/initial-booking/state/initial-booking.actions';
import { getUser } from '../../../../services/login-service/state/login-service.selector';
import { SplitioService } from '../../../../services/splitio-service/splitio.service';
import { Splits } from '../../../../services/splitio-service/splits';
import { initializeFlightSearchFormsState } from '../../../../shared/flight-search-forms/state/flight-search-forms.actions';
import { RootState } from '../../../../state/state';
import { TierStatusHelper } from '../../../../utils/tier-status-helper';

@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
  imports: [RouterModule, DirectivesModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class NavBarComponent implements OnInit {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly splitioService = inject(SplitioService);
  private readonly cdRef = inject(ChangeDetectorRef);

  @Input() menuItems: any;
  @Input() agentName: string;
  @Input() user: UserData;
  @Input() contentClass: string;
  @Input() menuId: string;
  @Output() openDrawerEvent = new EventEmitter<any>();
  @Output() userProfileClickEvent = new EventEmitter<string>();
  @Output() logoutEvent = new EventEmitter();

  tierStatusHelper = TierStatusHelper;

  attributes: SplitIO.Attributes;
  agentFeedbackTreatment: string;
  showBagJourneyMenuOption = false;

  ngOnInit(): void {
    // The SDK_READY event will only fire the first time the user lands on the guest tab (per login session),
    // so we need to listen to the SDK_READY event to fire the updateSplits() logic upon initial load.
    // All subsequent ngOnInit() calls will not fire the SDK_READY event since the SDK will already be initialized.
    // This will ensure a scenario like looking up a different PNR will also trigger the logic.
    this.splitioService.splitClient.on(this.splitioService.splitClient.Event.SDK_READY, () => {
      this.updateSplits();
    });
    // The SDK_UPDATE event will fire when a change has been made to a split. This is to account for when we turn
    // the split on/off for users, so that they detect the change in real time.
    this.splitioService.splitClient.on(this.splitioService.splitClient.Event.SDK_UPDATE, () => {
      this.updateSplits();
    });
    this.updateSplits();
  }

  openDrawerHelper(e: Event, tagName: string, id: string) {
    e.preventDefault();
    this.openDrawerEvent.emit({ tagName, id });
  }

  userProfileClick(e: Event, id: string) {
    e.preventDefault();
    this.userProfileClickEvent.emit(id);
  }

  logout(e: Event) {
    e.preventDefault();
    this.logoutEvent.emit();
  }

  updateSplits() {
    // Used for Split.io feature flagging
    // 1. Pull username off of user to send up as an attribute to the splits
    this.store.pipe(select(getUser), take(1)).subscribe((user) => (this.attributes = { username: user.username, homeCity: user.sabreAAA }));
    // 2. Get treatment. Treatment values will be 'on', 'off', or 'control'
    const agentFeedbackTreatment: SplitIO.Treatment = this.splitioService.splitClient.getTreatment(
      Splits.RAIN_Agent_Feedback,
      this.attributes
    );

    // 3. Evaluate agent feedback treatment.
    this.agentFeedbackTreatment = agentFeedbackTreatment;

    this.showBagJourneyMenuOption = this.user.isCBSAgent;

    // 4. Force manual change detection to update the template
    this.cdRef.markForCheck();
  }

  navigateToBookFlights(event: Event): void {
    event.preventDefault();
    this.store.dispatch(initializeInitialBookingState());
    this.store.dispatch(initializeFlightSearchFormsState());
  }
}
